/**
 * One WebSocket into one room. Reconnection policy lives in the app; this
 * class only connects, pings, parses and reports how it closed.
 */
import type { Look } from '../../../shared/cosmetics.ts'
import { MAX_FRAME, PING_MS, PROTOCOL_VERSION, type ClientMsg, type ServerMsg } from '../../../shared/protocol.ts'
import type { RoomId } from '../../../shared/world.ts'
import { API_HOST, API_SECURE } from '../config.ts'

export interface JoinParams { room: RoomId; name: string; look: Look; key: string; from?: RoomId; shard?: number; place?: string }

export interface NetEvents {
  message: (m: ServerMsg) => void
  open: () => void
  close: (code: number, reason: string, opened: boolean) => void
}

export class Net {
  private ws?: WebSocket
  private ping = 0
  private opened = false
  private closedByUs = false

  private ev: NetEvents

  constructor(ev: NetEvents) { this.ev = ev }

  connect(p: JoinParams): void {
    this.close()
    this.closedByUs = false
    this.opened = false
    const q = new URLSearchParams({ v: String(PROTOCOL_VERSION), room: p.room, name: p.name, look: JSON.stringify(p.look), key: p.key })
    if (p.from) q.set('from', p.from)
    if (p.shard) q.set('shard', String(p.shard))
    if (p.place) q.set('place', p.place)
    const ws = new WebSocket(`${API_SECURE ? 'wss' : 'ws'}://${API_HOST}/ws?${q}`)
    this.ws = ws
    ws.onopen = () => {
      if (ws !== this.ws) return
      this.opened = true
      this.ping = window.setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send('ping') }, PING_MS)
      this.ev.open()
    }
    ws.onmessage = (e) => {
      if (ws !== this.ws || typeof e.data !== 'string' || e.data === 'pong') return
      try { this.ev.message(JSON.parse(e.data) as ServerMsg) } catch { /* ignore a bad frame */ }
    }
    ws.onclose = (e) => {
      if (ws !== this.ws) return
      clearInterval(this.ping)
      this.ws = undefined
      if (!this.closedByUs) this.ev.close(e.code, e.reason, this.opened)
    }
  }

  send(m: ClientMsg): boolean {
    const s = JSON.stringify(m)
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || s.length > MAX_FRAME) return false
    this.ws.send(s)
    return true
  }

  get connected(): boolean { return this.ws?.readyState === WebSocket.OPEN }

  close(): void {
    this.closedByUs = true
    clearInterval(this.ping)
    const ws = this.ws
    this.ws = undefined
    if (ws && ws.readyState <= WebSocket.OPEN) ws.close(1000, 'bye')
  }
}

export async function fetchCensus(): Promise<{ total: number; rooms: Record<RoomId, { n: number; shards: Array<{ shard: number; n: number }> }> } | null> {
  try {
    const r = await fetch(`${API_SECURE ? 'https' : 'http'}://${API_HOST}/census`)
    return r.ok ? await r.json() : null
  } catch {
    return null
  }
}
